import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  ModalWrapper,
  ModalInnerWrapper,
  CloseIconWrapper,
  WarningTitleWrapper,
  NormalTitleWrapper,
  BorderTitleWrapper,
  ModalBody,
  ModalFooter,
  ConfirmButton,
  CancelButton,
} from "./atom";
import CloseIcon from "../../assets/svg/close-icon";
import WarningIcon from "../../assets/svg/warning";
import { Space } from "../space";
import { getTranslateNsT } from "../../i18n/common";
import { isEn } from "project-config/utils/env";
import { useClickOutside } from "project-config/hooks/useClickOutside";
import { ThemeType } from "../../constants";
import { SimpleInterpolation } from "styled-components";
import { getModalStyle } from "./theme";
import LoadingIcon from "../../assets/svg/loading";
import { createRoot } from "react-dom/client";

export function Modal({
  children,
  open,
  type = "normal",
  size = "normal",
  title = "",
  closeOnClickOutside = true,
  themeType,
  render,
  titleBorderColor,
  titleFontSize,
  cancelText,
  confirmText,
  confirmDisable,
  submitting,
  showClose = true,
  customCSS,
  bodyWidth,
  center,
  onOk,
  onClose = () => {},
}: ModalProps) {
  const t = getTranslateNsT(isEn() ? "en" : "zh");
  const ref = useRef();
  const ModalTitle = {
    [ModalType.Normal]: NormalTitle,
    [ModalType.Confirm]: NormalTitle,
    [ModalType.BorderTitle]: BorderTitle,
    [ModalType.Warning]: WarningTitle,
    [ModalType.Blank]: "",
  }[type];
  const isBlank = ModalType.Blank === type;
  const themeStyle = getModalStyle({
    type,
    themeType,
  });
  const titleProps = {
    titleBorderColor,
    titleFontSize,
    cssProperties: themeStyle.title,
  };
  const _confirmText = confirmText || t("confirm");
  const _cancelText = cancelText || t("cancel");

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [open]);
  useClickOutside(ref, onClose, open && closeOnClickOutside && !isBlank);

  if (!open) return null;
  return (
    <ModalWrapper cssProperties={themeStyle.global} customCSS={customCSS}>
      {isBlank && render ? (
        render
      ) : (
        <ModalInnerWrapper
          size={size}
          ref={ref}
          className="task-eva-modal-inner-wrapper"
        >
          {!render && (
            <>
              <>
                <ModalTitle {...titleProps} className="task-eva-modal-title">
                  {title}
                </ModalTitle>
                <ModalBody width={bodyWidth} center={center}>
                  {children}
                </ModalBody>
              </>
              <ModalFooter
                cssProperties={themeStyle.footer}
                className="task-eva-modal-footer"
              >
                {[ModalType.Warning, ModalType.Confirm].includes(type) && (
                  <Space gap={14}>
                    <CancelButton
                      onClick={() => onClose?.()}
                      className="modal-cancel-button"
                    >
                      {_cancelText}
                    </CancelButton>
                    <ConfirmButton
                      disabled={confirmDisable || submitting}
                      className="modal-confirm-button"
                      onClick={() => onOk?.()}
                    >
                      {_confirmText}
                      {submitting && <LoadingIcon />}
                    </ConfirmButton>
                  </Space>
                )}
                {type === ModalType.Normal && (
                  <ConfirmButton onClick={() => onOk?.()}>
                    {_confirmText}
                  </ConfirmButton>
                )}
              </ModalFooter>
            </>
          )}
          {render && render}
          {showClose && (
            <CloseIconWrapper
              className="close-icon-wrapper"
              type={type}
              cssProperties={themeStyle.closeIcon}
              onClick={() => onClose?.()}
            >
              <CloseIcon />
            </CloseIconWrapper>
          )}
        </ModalInnerWrapper>
      )}
    </ModalWrapper>
  );
}
export enum ModalType {
  Normal = "normal",
  BorderTitle = "border-title",
  Warning = "warning",
  Blank = "blank",
  Confirm = "confirm",
}
export type IModalType = `${ModalType}`;
export type IModalSize = "normal" | "middle";
export type ModalProps = PropsWithChildren<
  TitleProps & {
    open?: boolean;
    type?: IModalType;
    size?: IModalSize;
    title?: ReactNode;
    render?: React.ReactNode;
    closeOnClickOutside?: boolean;
    themeType?: ThemeType;
    confirmText?: string;
    cancelText?: string;
    confirmDisable?: boolean;
    submitting?: boolean;
    showClose?: boolean;
    showConfirm?: boolean;
    customCSS?: SimpleInterpolation;
    bodyWidth?: string;
    center?: boolean;
    onOk?: (params?: any) => void | Promise<any>;
    onClose?: (params?: any) => void | Promise<void>;
  }
>;

export function WarningTitle({ children, ...props }: TitleProps) {
  return (
    <WarningTitleWrapper {...props}>
      <WarningIcon />
      {children}
    </WarningTitleWrapper>
  );
}
export function NormalTitle({ children, ...props }: TitleProps) {
  return <NormalTitleWrapper {...props}>{children}</NormalTitleWrapper>;
}
export function BorderTitle({ children, ...props }: TitleProps) {
  return <BorderTitleWrapper {...props}>{children}</BorderTitleWrapper>;
}

export type TitleProps = PropsWithChildren<{
  titleBorderColor?: string;
  titleFontSize?: string;
  cssProperties?: CSSProperties;
}>;

export { CreateInterceptModal as CreateInterceptModal } from "./create-intercept-modal";

Modal.CancelButton = CancelButton;
Modal.ConfirmButton = ConfirmButton;
Modal.confirm = (props: ModalProps) => {
  const rootElement = document.body;
  if (!rootElement) return;

  const div = document.createElement("div");
  rootElement.append(div);

  const root = createRoot(div);

  const destroy = () => {
    root.unmount();
    rootElement.removeChild(div);
  };

  root.render(
    <Modal
      onClose={destroy}
      customCSS={{ zIndex: 100000, color: "#fff" }}
      closeOnClickOutside={false}
      {...props}
    />
  );
};
